/**
 * Plant stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const plantStickersPack: ToilePack = {
    slug: 'plant-stickers',
    category: 'sticker',
    name: 'Plant stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'monstera', label: 'Monstera', subject: 'a single monstera leaf with its split holes'},
        {key: 'cactus', label: 'Potted cactus', subject: 'a small round cactus in a terracotta pot with a pink flower'},
        {key: 'pothos', label: 'Hanging pothos', subject: 'a hanging pothos in a macramé holder trailing leaves'},
    ],
    styles: [
        {key: 'retro', label: 'Retro', style: 'retro sticker illustration, 1970s shapes, halftone grain, slight misregistration', palette: 'Mustard, rust, teal and cream', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'minimal', label: 'Minimal line', style: 'minimal single-line sticker, one clean continuous line, no fill', palette: 'Black line only, one accent colour', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a succulent instead', 'for a water bottle', 'as a matching pair'],
    directionHint: 'try: a succulent instead',
    seo: {
        title: 'Plant Stickers: Monstera, Cactus, Pothos',
        metaDescription: 'Nine houseplant stickers — a monstera leaf, a potted cactus, a hanging pothos — in retro, bold cartoon, and minimal line styles.',
        h1: 'Plant stickers: monstera, potted cactus, hanging pothos',
        tagline: 'Nine stickers, three houseplants, three styles each.',
        intro: 'The houseplant shelf, as die-cut stickers: a single monstera leaf with its split holes, a round cactus in a terracotta pot, and a hanging pothos trailing from a macramé holder. Each rendered retro, bold cartoon, and minimal single-line. Ask for a different plant, a watering-can scene, or a whole windowsill composition, and the same three styles carry it.',
        useCases: [
            'a water bottle or laptop lid',
            'a plant-care journal or planner',
            'a kitchen window or terrarium jar',
            'a gift for a plant person',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine houseplant stickers — a monstera leaf, a potted cactus, and a hanging pothos — in retro, bold cartoon, and minimal line finishes.'},
            {q: 'Can I get a different houseplant?', a: 'Yes — the direction field takes any variation, including a different plant, a watering-can scene, or a whole windowsill composition.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full A4 sheet.'},
            {q: 'Are these botanically accurate?', a: 'No — these are decorative illustrations styled for stickers, not a botanical reference.'},
        ],
    },
}
